import { useState, useEffect, useCallback } from 'react'
import { Lead, listLeads, saveLead } from '../../services/leads'
import { LeadForm, LeadFormValues } from '../../components/form/LeadForm'
import { LeadCard } from './LeadCard'

type Props = { active: boolean }

function todayKey() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function localDayKey(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function Leads({ active }: Props) {
  const [leads, setLeads] = useState<Lead[]>([])
  const [loaded, setLoaded] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const reload = useCallback(async () => {
    const list = await listLeads()
    setLeads(list)
    setLoaded(true)
  }, [])

  // Refresh whenever the card is brought to the foreground so new captures show up.
  useEffect(() => {
    if (active) reload()
  }, [active, reload])

  const selected = selectedId ? leads.find((l) => l.id === selectedId) ?? null : null

  const today = todayKey()
  const todayCount = leads.filter((l) => localDayKey(l.capturedAt) === today).length

  const handleSave = async (values: LeadFormValues) => {
    if (!selected) return
    await saveLead({ ...selected, ...values })
    await reload()
    setSelectedId(null)
  }

  return (
    <div className="flex h-full flex-col">
      {selected ? (
        <DetailView
          lead={selected}
          onBack={() => setSelectedId(null)}
          onSave={handleSave}
        />
      ) : (
        <ListView
          leads={leads}
          loaded={loaded}
          todayCount={todayCount}
          onOpen={setSelectedId}
        />
      )}
    </div>
  )
}

function ListView({
  leads,
  loaded,
  todayCount,
  onOpen,
}: {
  leads: Lead[]
  loaded: boolean
  todayCount: number
  onOpen: (id: string) => void
}) {
  return (
    <>
      <div className="flex items-end justify-between border-b border-white/5 px-5 py-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-theme-text-muted">
            Today
          </div>
          <div className="text-3xl font-semibold leading-none">{todayCount}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-[0.18em] text-theme-text-muted">
            Total
          </div>
          <div className="text-3xl font-semibold leading-none">{leads.length}</div>
        </div>
      </div>
      <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto px-4 py-3">
        {!loaded ? (
          <div className="py-10 text-center text-theme-text-muted">Loading…</div>
        ) : leads.length === 0 ? (
          <EmptyState />
        ) : (
          leads.map((lead) => (
            <LeadCard key={lead.id} lead={lead} onClick={() => onOpen(lead.id)} />
          ))
        )}
      </div>
    </>
  )
}

function DetailView({
  lead,
  onBack,
  onSave,
}: {
  lead: Lead
  onBack: () => void
  onSave: (v: LeadFormValues) => Promise<void>
}) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-white/5 px-3 py-2.5">
        <button
          onClick={onBack}
          type="button"
          className="flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-theme-text-subtle hover:text-white"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Back
        </button>
        <div className="text-xs text-theme-text-muted">
          Captured {new Date(lead.capturedAt).toLocaleDateString()}
        </div>
      </div>
      <LeadForm
        initial={lead}
        onSubmit={onSave}
        submitLabel="Save changes"
        onCancel={onBack}
        showSegments
        showNotes
      />
    </div>
  )
}

function EmptyState() {
  return (
    <div className="py-14 text-center text-theme-text-muted">
      <div className="text-3xl">📇</div>
      <div className="mt-2 text-sm">No leads yet</div>
      <div className="mt-1 text-xs opacity-70">
        Capture your first one from the Capture card
      </div>
    </div>
  )
}
